import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';

// Componente para manejo de ubicaciones en mapas
const MapLocation = ({ initialLocation, onLocationChange }) => {
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [searchBox, setSearchBox] = useState(null);

  // Cargar la API de Google Maps
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    // Función para cargar el script de Google Maps
    const loadGoogleMapsScript = () => {
      const googleMapsScript = document.createElement('script');
      googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=places`;
      googleMapsScript.async = true;
      googleMapsScript.defer = true;
      googleMapsScript.onload = () => setMapLoaded(true);
      document.body.appendChild(googleMapsScript);
    };

    loadGoogleMapsScript();
  }, []);

  // Inicializar el mapa cuando la API está cargada
  useEffect(() => {
    if (!mapLoaded) return;
    
    const defaultPosition = initialLocation && initialLocation.lat && initialLocation.lng
      ? { lat: parseFloat(initialLocation.lat), lng: parseFloat(initialLocation.lng) }
      : { lat: -12.046374, lng: -77.042793 }; // Lima, Perú por defecto

    // Crear el mapa
    const newMap = new window.google.maps.Map(mapRef.current, {
      center: defaultPosition,
      zoom: 15,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }]
        }
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    // Crear el marcador
    const newMarker = new window.google.maps.Marker({
      position: defaultPosition,
      map: newMap,
      draggable: true,
      animation: window.google.maps.Animation.DROP
    });

    // Crear el searchbox para búsqueda de ubicaciones
    const input = searchBoxRef.current;
    const newSearchBox = new window.google.maps.places.SearchBox(input);
    
    // Limitar la búsqueda a la vista actual del mapa
    newMap.addListener('bounds_changed', () => {
      newSearchBox.setBounds(newMap.getBounds());
    });

    // Actualizar posición cuando se arrastra el marcador
    newMarker.addListener('dragend', () => {
      const position = newMarker.getPosition();
      
      // Hacer geocoding inverso para obtener la dirección
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const formattedAddress = results[0].formatted_address;
          // Simplificar la dirección (por ejemplo, quitar el país y código postal)
          const addressComponents = results[0].address_components;
          const city = addressComponents.find(comp => comp.types.includes('locality'))?.long_name || '';
          const district = addressComponents.find(comp => comp.types.includes('administrative_area_level_2'))?.long_name || '';
          const simplifiedAddress = city && district ? `${city}, ${district}` : formattedAddress;
          
          // Notificar al componente padre
          onLocationChange({
            lat: position.lat(),
            lng: position.lng(),
            address: formattedAddress,
            simplified_address: simplifiedAddress
          });
        }
      });
    });

    // Manejar cambios en el searchbox
    newSearchBox.addListener('places_changed', () => {
      const places = newSearchBox.getPlaces();
      
      if (places.length === 0) return;
      
      const place = places[0];
      
      if (!place.geometry || !place.geometry.location) return;
      
      // Mover el mapa a la ubicación seleccionada
      newMap.setCenter(place.geometry.location);
      newMarker.setPosition(place.geometry.location);
      
      // Simplificar la dirección
      const addressComponents = place.address_components || [];
      const city = addressComponents.find(comp => comp.types.includes('locality'))?.long_name || '';
      const district = addressComponents.find(comp => comp.types.includes('administrative_area_level_2'))?.long_name || '';
      const simplifiedAddress = city && district ? `${city}, ${district}` : place.formatted_address;
      
      // Notificar al componente padre
      onLocationChange({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: place.formatted_address,
        simplified_address: simplifiedAddress
      });
    });

    // Establecer ubicación inicial en el input si existe
    if (initialLocation && initialLocation.address) {
      input.value = initialLocation.address;
    }

    // Guardar referencias
    setMap(newMap);
    setMarker(newMarker);
    setSearchBox(newSearchBox);

    // Limpiar
    return () => {
      if (newMarker) {
        window.google.maps.event.clearListeners(newMarker, 'dragend');
      }
      if (newSearchBox) {
        window.google.maps.event.clearListeners(newSearchBox, 'places_changed');
      }
    };
  }, [mapLoaded, initialLocation, onLocationChange]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          ref={searchBoxRef}
          type="text"
          placeholder="Buscar ubicación..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
        />
        <MapPin className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
      </div>
      
      <div 
        ref={mapRef} 
        className="h-64 w-full rounded-md border border-gray-300 shadow-sm"
      ></div>
      
      <p className="text-xs text-gray-500">
        Busca tu ubicación o arrastra el marcador para una ubicación precisa
      </p>
    </div>
  );
};

export default MapLocation;